import { useTheme } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { CreateCSSProperties, createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React, { PropsWithChildren, ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { HeaderTitle, Space, Submittable } from '@/components';
import { BaseContainer, HeaderBox, LogoutButton, LogoutLink, MainButton } from '@/layout/components';
import { withBackURL } from '@/layout/components/BackButton';
import { TitleVariant } from '@/layout/components/HeaderBox';
import { MainButtonProps } from '@/layout/components/MainButton';
import { TitleButtonProp } from '@/layout/components/TitleButton';
import { makeSelectIsFrameView, makeSelectIsMobileView, makeSelectPartner } from '@/model/global/selectors';

const useStyles = makeStyles((theme: Theme) => {
  const baseContainer: CreateCSSProperties = {
    overflowX: 'auto',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  };
  const formContent: CreateCSSProperties = baseContainer;
  const formContainer: CreateCSSProperties = {
    ...baseContainer,
    color: theme.colors.primary,
    backgroundColor: theme.colors.primaryBackground,
    margin: 0,
    padding: 0,
  };
  const footer: CreateCSSProperties = {
    marginTop: 'auto',
    flexGrow: 0,
    flexShrink: 0,
    overflow: 'visible',
  };
  return createStyles({
    header: {
      paddingTop: theme.spacing(),
      marginBottom: theme.spacing(3),
    },
    footer: {
      ...footer,
      paddingTop: theme.spacing(2.5),
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      paddingBottom: theme.spacing(4),
    },
    footerMobile: {
      ...footer,
      paddingTop: theme.spacing(2.5),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
    formContainer: {
      ...formContainer,
      width: theme.MainContainer.width,
    },
    formContainerMobile: {
      ...formContainer,
      maxWidth: 'unset',
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
    },
    formContent: {
      ...formContent,
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
    formContentMobile: {
      ...formContent,
      paddingTop: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  });
});

export interface MainLayoutProps extends PropsWithChildren<unknown> {
  'data-test'?: string;
  containerId?: string;
  content?: {
    prefix?: ReactNode;
    className?: string;
    contentClassName?: string;
  };
  container?: {
    prefix?: ReactNode;
    className?: string;
  };
  minHeight?: number | 'unset';
  height?: number | 'unset';
  header?: {
    className?: string;
    title?: ReactNode;
    titleVariant?: TitleVariant;
    titlePostfix?: ReactNode;
    postfix?: ReactNode;
    noLogo?: boolean;
  };
  buttons?: {
    back?: string | TitleButtonProp;
    logout?: ReactNode;
    main?: MainButtonProps;
  };
  footer?: {
    className?: string;
    component?: ReactNode;
  };
}

const selectPartner = makeSelectPartner();
const selectIsMobile = makeSelectIsMobileView();
const selectIsFrame = makeSelectIsFrameView();

const headerHeight = 68;
const footerHeight = 150;

const MainLayout: React.FC<MainLayoutProps> = ({
  header,
  content,
  container,
  containerId,
  children,
  buttons,
  footer,
  height,
  minHeight,
  'data-test': dataTest,
}) => {
  const classes = useStyles();
  const baseClasses = useTheme();
  const partner = useSelector(selectPartner);
  const isMobile = useSelector(selectIsMobile);
  const isFrame = useSelector(selectIsFrame);
  const logoutPosition: 'header' | 'bottom' | undefined = useMemo(() => {
    if (isFrame) {
      return undefined;
    }
    if (buttons?.logout) {
      return isMobile && !header?.titlePostfix ? 'header' : 'bottom';
    }
    return undefined;
  }, [buttons, header, isFrame, isMobile]);
  const withPartnerLogo = !header?.noLogo && !isMobile && partner?.iconUrl;
  const mainHeight =
    // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
    // prettier-ignore
    (height && height !== 'unset' ? height : baseClasses.MainContainer.height)
    + (withPartnerLogo ? headerHeight : 0) + (footer ? footerHeight : 0);
  return (
    <BaseContainer
      height={!isMobile ? mainHeight : undefined}
      boxClassname={container?.className}
      containerPrefix={container?.prefix}
    >
      <HeaderBox
        data-test={dataTest && `${dataTest}-header`}
        variant={header?.titleVariant || !isMobile ? 'h1' : 'h2'}
        title={
          // prettier-ignore
          header?.title || header?.postfix ? (
            <HeaderTitle title={header?.title} postfix={header?.titlePostfix} />
          ) : undefined
        }
        buttons={{
          leftTitle: typeof buttons?.back === 'string' ? withBackURL(buttons.back as string) : buttons?.back,
          rightTitle: logoutPosition === 'header' ? LogoutButton : undefined,
        }}
        className={header?.className}
        extra={header?.postfix}
        logo={
          withPartnerLogo && partner?.iconUrl
            ? {
                src: partner.iconUrl,
                alt: partner.name || 'partner',
              }
            : undefined
        }
      />
      <Submittable
        style={{ minHeight: isMobile ? minHeight : undefined }}
        onSubmit={buttons?.main?.submit ? buttons?.main?.onAct : undefined}
        className={clsx(content?.className, isMobile ? classes.formContainerMobile : classes.formContainer)}
      >
        {content?.prefix}
        <Space
          direction="vertical"
          size={3}
          id={containerId}
          className={clsx(content?.contentClassName, isMobile ? classes.formContentMobile : classes.formContent)}
        >
          {children}
        </Space>
        {(footer || buttons?.main || logoutPosition === 'bottom') && (
          <Space
            direction="vertical"
            size={2.5}
            className={clsx(isMobile ? classes.footerMobile : classes.footer, footer?.className)}
          >
            {footer?.component}
            {buttons?.main && <MainButton data-test={dataTest && `${dataTest}-submit`} {...buttons.main} />}
            {logoutPosition === 'bottom' && (
              <Space size={0} style={{ justifyContent: 'center' }}>
                <LogoutLink data-test={dataTest && `${dataTest}-logout`}>{buttons?.logout}</LogoutLink>
              </Space>
            )}
          </Space>
        )}
      </Submittable>
    </BaseContainer>
  );
};

export default MainLayout;
